import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import Header from './Header';
import { apiService } from '../services/api';
import TrackerSidebar, { SavedView, FollowRow } from './tracker/TrackerSidebar';
import TrackerFeed from './tracker/TrackerFeed';
import TrackerTimeline from './tracker/TrackerTimeline';
import TrackerSearchResults from './tracker/TrackerSearchResults';
import TrackerLoadingPanel from './tracker/TrackerLoadingPanel';
import TrackerDrawer from './tracker/TrackerDrawer';
import {
  TrialTrackerEvent,
  TrialTrackerTrial,
  TrialTrackerAssumptions,
  loadTrackerAssumptions,
  saveTrackerAssumptions,
  DEFAULT_TRACKER_ASSUMPTIONS,
  TYPE_META,
  TrialTrackerIndication,
  TrialTrackerSearchMode,
  TrialTrackerSearchResult,
  groupByIndication,
  suggestSearchQueryForIndication,
  formatScrapeDate,
  sponsorsMatch,
  matchesTrialStatusFilter,
  DEFAULT_TRIAL_STATUS_FILTERS,
  isMultiFilterUnrestricted,
  parseSavedMultiFilter,
  serializeMultiFilter,
  toggleMultiFilter,
  mergeFeedWithCurrentTrials,
  isPersistedFeedEventId,
} from '../utils/trialTrackerTimeline';

type ViewMode = 'feed' | 'timeline';

const ACTIVE_WATCHLIST_KEY = 'ruby_trial_tracker_active_watchlist';

function readActiveWatchlistId(): number | null {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('watchlist');
    if (fromUrl && Number.isFinite(Number(fromUrl))) return Number(fromUrl);
    const raw = localStorage.getItem(ACTIVE_WATCHLIST_KEY);
    if (raw && Number.isFinite(Number(raw))) return Number(raw);
  } catch {
    // ignore storage / URL errors
  }
  return null;
}

function writeActiveWatchlistId(id: number | null) {
  try {
    if (id == null) localStorage.removeItem(ACTIVE_WATCHLIST_KEY);
    else localStorage.setItem(ACTIVE_WATCHLIST_KEY, String(id));
  } catch {
    // ignore
  }
  try {
    const params = new URLSearchParams(window.location.search);
    if (id == null) params.delete('watchlist');
    else params.set('watchlist', String(id));
    const qs = params.toString();
    const next = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
    window.history.replaceState({}, '', next);
  } catch {
    // ignore
  }
}

const TrialTrackerView: React.FC = () => {
  const [loading, setLoading] = useState(true);
  /** Soft refresh when switching indications (keeps shell visible). */
  const [feedRefreshing, setFeedRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const initialLoadDoneRef = useRef(false);
  const skipIndicationFeedRef = useRef(true);
  const watchlistRestoredRef = useRef(false);

  const [events, setEvents] = useState<TrialTrackerEvent[]>([]);
  const [feedTrials, setFeedTrials] = useState<TrialTrackerTrial[]>([]);
  const [trialsTruncated, setTrialsTruncated] = useState(false);
  const [trials, setTrials] = useState<TrialTrackerTrial[]>([]);
  const [follows, setFollows] = useState<FollowRow[]>([]);
  const [savedViews, setSavedViews] = useState<SavedView[]>([]);
  const [alertTypesOff, setAlertTypesOff] = useState<Record<string, boolean>>({});
  const [deliveryMode, setDeliveryMode] = useState<'Instant' | 'Daily' | 'Weekly'>('Weekly');

  const [view, setView] = useState<ViewMode>('feed');
  const [catFilter, setCatFilter] = useState<string[]>(['all']);
  const [phaseFilter, setPhaseFilter] = useState<string[]>(['all']);
  const [statusFilter, setStatusFilter] = useState<string[]>(DEFAULT_TRIAL_STATUS_FILTERS);
  const [sourceFilter, setSourceFilter] = useState<string[]>(['all']);
  const [query, setQuery] = useState('');
  const [favOnly, setFavOnly] = useState(false);
  const [followedOnly, setFollowedOnly] = useState(false);
  const [selectedNct, setSelectedNct] = useState<string | null>(null);
  const [assumptions, setAssumptions] = useState<TrialTrackerAssumptions>(DEFAULT_TRACKER_ASSUMPTIONS);
  const [priorityOverrides, setPriorityOverrides] = useState<Record<string, boolean>>({});

  const [indications, setIndications] = useState<TrialTrackerIndication[]>([]);
  const [lastScrapeAt, setLastScrapeAt] = useState<string | null>(null);
  const [indicationFilter, setIndicationFilter] = useState<string[]>(() => {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('indication');
    if (!raw || raw === 'all') return [];
    return raw.split(',').map((s) => s.trim()).filter(Boolean);
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [submittedQuery, setSubmittedQuery] = useState('');
  const [searchMode, setSearchMode] = useState<TrialTrackerSearchMode>('all');
  const [searchResults, setSearchResults] = useState<TrialTrackerSearchResult[] | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [searchSummary, setSearchSummary] = useState<string | null>(null);
  const [followedTrials, setFollowedTrials] = useState<TrialTrackerSearchResult[] | null>(null);
  const [followedLoading, setFollowedLoading] = useState(false);
  const [followedError, setFollowedError] = useState<string | null>(null);
  const searchAbortRef = useRef<AbortController | null>(null);

  const [isSuperuser, setIsSuperuser] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshResult, setRefreshResult] = useState<{ checked: number; events_created: number; error?: string } | null>(null);

  const [sponsorFilter, setSponsorFilter] = useState<string | null>(null);
  const [activeWatchlistId, setActiveWatchlistId] = useState<number | null>(() => readActiveWatchlistId());

  const clearActiveWatchlist = useCallback(() => {
    if (activeWatchlistId == null) return;
    setActiveWatchlistId(null);
    writeActiveWatchlistId(null);
  }, [activeWatchlistId]);

  /** Followed-only is intentional — any other browse/filter action exits it. */
  const handleSetIndicationFilter = useCallback((values: string[]) => {
    setFollowedOnly(false);
    clearActiveWatchlist();
    setIndicationFilter(values);
  }, [clearActiveWatchlist]);

  const handleToggleCatFilter = useCallback((key: string) => {
    setFollowedOnly(false);
    clearActiveWatchlist();
    setCatFilter((prev) => toggleMultiFilter(prev, key));
  }, [clearActiveWatchlist]);

  const handleTogglePhaseFilter = useCallback((key: string) => {
    setFollowedOnly(false);
    clearActiveWatchlist();
    setPhaseFilter((prev) => toggleMultiFilter(prev, key));
  }, [clearActiveWatchlist]);

  const handleToggleStatusFilter = useCallback((key: string) => {
    setFollowedOnly(false);
    clearActiveWatchlist();
    setStatusFilter((prev) => toggleMultiFilter(prev, key));
  }, [clearActiveWatchlist]);

  const handleToggleSourceFilter = useCallback((key: string) => {
    setFollowedOnly(false);
    clearActiveWatchlist();
    setSourceFilter((prev) => toggleMultiFilter(prev, key));
  }, [clearActiveWatchlist]);

  useEffect(() => {
    setAssumptions(loadTrackerAssumptions());
  }, []);

  useEffect(() => {
    apiService.getCurrentUser().then((u: any) => setIsSuperuser(Boolean(u?.is_superuser))).catch(() => {});
  }, []);

  const loadFeed = useCallback(async (indicationIds?: string[]) => {
    const ids = (indicationIds || []).filter((id) => id && id !== 'all');
    const scoped = ids.length > 0;
    const feedRes = await apiService.fetchTrialTrackerFeed({
      perIndication: scoped ? 200 : 10,
      trialPerIndication: scoped ? 2000 : 100,
      indicationIds: ids,
    });
    setEvents(feedRes?.events || []);
    setFeedTrials(feedRes?.trials || []);
    setTrialsTruncated(Boolean(feedRes?.trials_truncated));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [followsRes, viewsRes, settingsRes, indicationsRes] = await Promise.all([
        apiService.fetchTrialTrackerFollows(),
        apiService.fetchTrialTrackerSavedViews(),
        apiService.getTrialTrackerSettings(),
        apiService.fetchTrialTrackerIndications(),
      ]);
      await loadFeed(indicationFilter);
      setFollows(followsRes?.follows || []);
      setSavedViews(viewsRes?.views || []);
      setAlertTypesOff(settingsRes?.alert_types_off || {});
      setDeliveryMode(settingsRes?.delivery_mode || 'Weekly');
      setIndications(indicationsRes?.indications || []);
      setLastScrapeAt(indicationsRes?.last_scrape_at || null);
      initialLoadDoneRef.current = true;
    } catch (e) {
      setError('Failed to load the trial tracker. Please try again.');
      // eslint-disable-next-line no-console
      console.error(e);
    } finally {
      setLoading(false);
    }
    // Initial indicationFilter only — subsequent changes handled by dedicated effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadFeed]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!initialLoadDoneRef.current) return;
    if (skipIndicationFeedRef.current) {
      skipIndicationFeedRef.current = false;
      return;
    }
    let cancelled = false;
    (async () => {
      setFeedRefreshing(true);
      try {
        await loadFeed(indicationFilter);
      } catch (e) {
        if (!cancelled) console.error(e);
      } finally {
        if (!cancelled) setFeedRefreshing(false);
      }
    })();
    return () => { cancelled = true; };
  }, [indicationFilter, loadFeed]);

  const loadFollowedTrials = useCallback(async () => {
    setFollowedLoading(true);
    setFollowedError(null);
    try {
      const res = await apiService.fetchTrialTrackerFollowedTrials(200);
      if (res?.success) {
        setFollowedTrials(res.trials || []);
      } else {
        setFollowedError(res?.error || 'Failed to load followed trials.');
        setFollowedTrials(null);
      }
    } catch {
      setFollowedError('Failed to load followed trials.');
      setFollowedTrials(null);
    } finally {
      setFollowedLoading(false);
    }
  }, []);

  useEffect(() => {
    if (followedOnly && submittedQuery.trim().length < 2) {
      loadFollowedTrials();
    } else if (!followedOnly) {
      setFollowedTrials(null);
      setFollowedError(null);
    }
  }, [followedOnly, submittedQuery, follows, loadFollowedTrials]);

  const phaseOptions = useMemo(() => {
    const set = new Set<string>();
    trials.forEach((t) => { if (t.phase) set.add(t.phase); });
    feedTrials.forEach((t) => { if (t.phase) set.add(t.phase); });
    events.forEach((e) => { if (e.phase) set.add(e.phase); });
    return Array.from(set).sort();
  }, [trials, feedTrials, events]);

  const sourceOptions = useMemo(() => [] as string[], []);

  const followSuggestions = useMemo(() => {
    const assets = new Set<string>();
    const indications = new Set<string>();
    trials.forEach((t) => {
      if (t.asset_name) assets.add(t.asset_name);
      if (t.indication) indications.add(t.indication);
      if (t.indication_name) indications.add(t.indication_name);
    });
    feedTrials.forEach((t) => {
      if (t.asset_name) assets.add(t.asset_name);
      if (t.indication) indications.add(t.indication);
      if (t.indication_name) indications.add(t.indication_name);
    });
    events.forEach((e) => {
      if (e.asset_name) assets.add(e.asset_name);
      if (e.indication) indications.add(e.indication);
      if (e.indication_name) indications.add(e.indication_name);
    });
    (searchResults || []).forEach((r) => {
      if (r.asset_name) assets.add(r.asset_name);
      if (r.indication_name) indications.add(r.indication_name);
    });
    return [
      ...Array.from(assets).sort().map((v) => ({ type: 'asset' as const, value: v })),
      ...Array.from(indications).sort().map((v) => ({ type: 'indication' as const, value: v })),
    ];
  }, [trials, feedTrials, events, searchResults]);

  const followedValues = useMemo(
    () => new Set(follows.map((f) => f.follow_value.trim().toLowerCase())),
    [follows],
  );

  const matchesFollow = useCallback((item: {
    asset_name?: string | null;
    indication?: string | null;
    indication_name?: string | null;
    intervention_names?: string[] | null;
  }) => {
    if (!followedOnly || followedValues.size === 0) return true;
    const vals = followedValues;
    const hit = (s?: string | null) => Boolean(s && vals.has(s.trim().toLowerCase()));
    if (hit(item.asset_name) || hit(item.indication) || hit(item.indication_name)) return true;
    return (item.intervention_names || []).some((intr) => hit(intr));
  }, [followedOnly, followedValues]);

  const matchesStatus = useCallback(
    (status?: string | null) => matchesTrialStatusFilter(status, statusFilter),
    [statusFilter],
  );

  const matchesCat = useCallback((cat: string) => {
    if (isMultiFilterUnrestricted(catFilter)) return true;
    return catFilter.includes(cat);
  }, [catFilter]);

  const matchesPhase = useCallback((phase?: string | null) => {
    if (isMultiFilterUnrestricted(phaseFilter)) return true;
    return Boolean(phase && phaseFilter.includes(phase));
  }, [phaseFilter]);

  const matchesSource = useCallback((nctId: string) => {
    if (isMultiFilterUnrestricted(sourceFilter)) return true;
    return sourceFilter.some((src) => {
      if (src === 'CT.gov') return /^NCT/i.test(nctId);
      if (src === 'ChiCTR') return /^ChiCTR/i.test(nctId);
      return false;
    });
  }, [sourceFilter]);
  const matchesQuery = useCallback((haystack: (string | null | undefined)[]) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return haystack.filter(Boolean).join(' ').toLowerCase().includes(q);
  }, [query]);

  const matchesIndication = useCallback((indicationId?: number | null) => {
    if (indicationFilter.length === 0) return true;
    return indicationFilter.includes(String(indicationId ?? ''));
  }, [indicationFilter]);

  const matchesSponsor = useCallback((sponsor?: string | null) => {
    if (!sponsorFilter) return true;
    return sponsorsMatch(sponsor, sponsorFilter);
  }, [sponsorFilter]);

  const filteredEvents = useMemo(() => {
    return events.filter((e) => {
      if (alertTypesOff[e.event_type]) return false;
      const meta = TYPE_META[e.event_type];
      if (meta && !matchesCat(meta.cat)) return false;
      if (!matchesPhase(e.phase)) return false;
      if (!matchesSource(e.nct_id)) return false;
      if (!matchesIndication(e.indication_id)) return false;
      if (!matchesSponsor(e.sponsor)) return false;
      if (favOnly && !e.favorited) return false;
      if (!matchesFollow(e)) return false;
      if (!matchesStatus(e.status)) return false;
      if (!matchesQuery([e.asset_name, e.trial_name, e.nct_id, e.sponsor, e.phase, e.indication, e.headline])) return false;
      return true;
    });
  }, [events, alertTypesOff, matchesCat, matchesPhase, matchesSource, matchesIndication, matchesSponsor, favOnly, matchesFollow, matchesStatus, matchesQuery]);

  const filteredFeedTrials = useMemo(() => {
    return feedTrials.filter((t) => {
      if (!matchesPhase(t.phase)) return false;
      if (!matchesSource(t.nct_id)) return false;
      if (!matchesIndication(t.indication_id)) return false;
      if (!matchesSponsor(t.sponsor)) return false;
      if (favOnly && !t.favorited) return false;
      if (!matchesFollow(t)) return false;
      if (!matchesStatus(t.status)) return false;
      if (!matchesQuery([t.asset_name, t.trial_name, t.nct_id, t.sponsor, t.phase, t.indication])) return false;
      return true;
    });
  }, [feedTrials, matchesPhase, matchesSource, matchesIndication, matchesSponsor, favOnly, matchesFollow, matchesStatus, matchesQuery]);

  const filteredSearchResults = useMemo(() => {
    if (!searchResults) return [];
    const q = query.trim().toLowerCase();
    return searchResults.filter((r) => {
      if (!matchesStatus(r.overall_status || r.status)) return false;
      if (!q) return true;
      return [
        r.asset_name, r.trial_name, r.full_title, r.nct_id, r.sponsor, r.phases,
        r.indication_name, r.indication_details, r.brief_summary,
        ...(r.intervention_names || []), ...(r.primary_outcome_measures || []),
      ].filter(Boolean).join(' ').toLowerCase().includes(q);
    });
  }, [searchResults, query, matchesStatus]);

  const filteredFollowedTrials = useMemo(() => {
    if (!followedTrials) return [];
    const q = query.trim().toLowerCase();
    const followVals = new Set(follows.map((f) => f.follow_value.trim().toLowerCase()).filter(Boolean));
    return followedTrials.filter((r) => {
      if (!matchesStatus(r.overall_status || r.status)) return false;
      // Keep display synced with Following chips — drop trials that don't match a follow.
      if (followVals.size > 0) {
        const hay = [
          r.asset_name,
          r.indication_name,
          r.indication_details,
          ...(r.intervention_names || []),
          r.trial_name,
          r.full_title,
        ].filter(Boolean).join(' ').toLowerCase();
        const matchesAFollow = [...followVals].some((v) => hay.includes(v));
        if (!matchesAFollow) return false;
      }
      if (!q) return true;
      return [
        r.asset_name, r.trial_name, r.full_title, r.nct_id, r.sponsor, r.phases,
        r.indication_name, r.indication_details, r.brief_summary,
        ...(r.intervention_names || []), ...(r.primary_outcome_measures || []),
      ].filter(Boolean).join(' ').toLowerCase().includes(q);
    });
  }, [followedTrials, query, matchesStatus, follows]);

  const displayEvents = useMemo(() => {
    const base = filteredEvents;
    if (!isMultiFilterUnrestricted(catFilter)) return base;
    return mergeFeedWithCurrentTrials(base, filteredFeedTrials);
  }, [filteredEvents, catFilter, filteredFeedTrials]);

  const showGroupHeaders = indicationFilter.length === 0;

  const sortedDisplayEvents = useMemo(
    () => [...displayEvents].sort((a, b) => (b.detected_at || '').localeCompare(a.detected_at || '')),
    [displayEvents],
  );

  const groupedEvents = useMemo(() => groupByIndication(sortedDisplayEvents), [sortedDisplayEvents]);

  // Same population as the feed table (feedTrials), not the separate /timeline endpoint.
  const timelineTrials = useMemo(() => {
    if (!showGroupHeaders) return filteredFeedTrials;
    return [...filteredFeedTrials].sort((a, b) => {
      const cmp = (a.indication_name || 'Other / unspecified').localeCompare(b.indication_name || 'Other / unspecified');
      if (cmp !== 0) return cmp;
      return (a.study_start_date || '').localeCompare(b.study_start_date || '');
    });
  }, [filteredFeedTrials, showGroupHeaders]);

  const searchResultToTrial = useCallback((hit: TrialTrackerSearchResult): TrialTrackerTrial => ({
    nct_id: hit.nct_id,
    trial_name: hit.trial_name || hit.full_title,
    full_title: hit.full_title,
    phase: hit.phases,
    status: hit.overall_status || hit.status,
    sponsor: hit.organization_full_name || hit.sponsor,
    enrollment: hit.enrollment != null ? String(hit.enrollment) : null,
    study_start_date: hit.study_start_date,
    primary_completion_date: hit.primary_completion_date,
    study_completion_date: hit.study_completion_date || hit.completion_date,
    indication: hit.indication_details,
    indication_name: hit.indication_name,
    asset_name: hit.asset_name,
    designations: [],
    favorited: false,
  }), []);

  const followedTimelineTrials = useMemo(() => {
    const byNct = new Map<string, TrialTrackerTrial>();
    feedTrials.forEach((t) => byNct.set(t.nct_id, t));
    trials.forEach((t) => byNct.set(t.nct_id, t));
    const list = filteredFollowedTrials.map((hit) => {
      const converted = searchResultToTrial(hit);
      const existing = byNct.get(hit.nct_id);
      if (!existing) return converted;
      return {
        ...converted,
        ...existing,
        // Prefer followed-API dates/status when present (fresher search payload).
        trial_name: converted.trial_name || existing.trial_name,
        full_title: converted.full_title || existing.full_title,
        phase: converted.phase || existing.phase,
        status: converted.status || existing.status,
        sponsor: converted.sponsor || existing.sponsor,
        study_start_date: converted.study_start_date || existing.study_start_date,
        primary_completion_date: converted.primary_completion_date || existing.primary_completion_date,
        study_completion_date: converted.study_completion_date || existing.study_completion_date,
        indication_name: converted.indication_name || existing.indication_name,
        asset_name: converted.asset_name || existing.asset_name,
      };
    });
    if (!showGroupHeaders) return list;
    return [...list].sort((a, b) => {
      const cmp = (a.indication_name || 'Other / unspecified').localeCompare(b.indication_name || 'Other / unspecified');
      if (cmp !== 0) return cmp;
      return (a.study_start_date || '').localeCompare(b.study_start_date || '');
    });
  }, [filteredFollowedTrials, feedTrials, trials, searchResultToTrial, showGroupHeaders]);

  const searchActive = submittedQuery.trim().length >= 2;
  const followedViewActive = followedOnly && !searchActive;

  const resultCount = searchActive
    ? filteredSearchResults.length
    : followedViewActive
      ? (view === 'timeline' ? followedTimelineTrials.length : filteredFollowedTrials.length)
      : (view === 'feed' ? displayEvents.length : timelineTrials.length);

  const snapshotCount = view === 'feed' && !searchActive && !followedViewActive
    ? displayEvents.filter((e) => e.event_type === 'CURRENT').length
    : 0;
  const alertCount = view === 'feed' && !searchActive && !followedViewActive
    ? displayEvents.length - snapshotCount
    : 0;

  const unreadCount = filteredEvents.filter((e) => !e.read).length;

  const selectedIndicationMeta = useMemo(
    () => (indicationFilter.length === 1
      ? indications.find((i) => String(i.id) === indicationFilter[0]) || null
      : null),
    [indications, indicationFilter],
  );

  const handleToggleFavorite = async (nctId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setEvents((prev) => prev.map((ev) => (ev.nct_id === nctId ? { ...ev, favorited: !ev.favorited } : ev)));
    setFeedTrials((prev) => prev.map((t) => (t.nct_id === nctId ? { ...t, favorited: !t.favorited } : t)));
    setTrials((prev) => prev.map((t) => (t.nct_id === nctId ? { ...t, favorited: !t.favorited } : t)));
    try {
      await apiService.toggleTrialTrackerFavorite(nctId);
    } catch {
      load();
    }
  };

  const handleSelectFeedItem = async (nctId: string) => {
    setSelectedNct(nctId);
    const ev = events.find((e) => e.nct_id === nctId);
    const fromFeed = feedTrials.find((t) => t.nct_id === nctId);
    if (!trials.some((t) => t.nct_id === nctId)) {
      if (fromFeed) upsertTrial(fromFeed);
      else if (ev) upsertTrial(eventToTrial(ev));
    }
    const unreadIds = events
      .filter((e) => e.nct_id === nctId && !e.read && isPersistedFeedEventId(e.id))
      .map((e) => e.id)
      .filter((id): id is number => typeof id === 'number' && id > 0);
    if (unreadIds.length) {
      setEvents((prev) => prev.map((e) => (e.nct_id === nctId ? { ...e, read: true } : e)));
      try {
        await Promise.all(unreadIds.map((id) => apiService.markTrialTrackerAlertRead(id)));
      } catch {
        // best-effort; UI already optimistically marked read
      }
    }
  };

  const handleMarkAllRead = async () => {
    setEvents((prev) => prev.map((e) => ({ ...e, read: true })));
    try { await apiService.markAllTrialTrackerAlertsRead(); } catch { load(); }
  };

  const handleAddFollow = async (type: 'asset' | 'indication', value: string) => {
    try {
      const res = await apiService.addTrialTrackerFollow(type, value);
      if (res?.follow) setFollows((prev) => [...prev, res.follow]);
      if (followedOnly) loadFollowedTrials();
    } catch {
      // no-op — leave state as-is on failure
    }
  };

  const handleRemoveFollow = async (id: number) => {
    setFollows((prev) => prev.filter((f) => f.id !== id));
    try {
      await apiService.removeTrialTrackerFollow(id);
      if (followedOnly) loadFollowedTrials();
    } catch { load(); }
  };

  const applySavedView = useCallback((v: SavedView) => {
    setFollowedOnly(false);
    setCatFilter(parseSavedMultiFilter(v.cat_filter));
    setPhaseFilter(parseSavedMultiFilter(v.phase_filter));
    setSourceFilter(parseSavedMultiFilter(v.source_filter));
    setActiveWatchlistId(v.id);
    writeActiveWatchlistId(v.id);
  }, []);

  // Re-apply the last selected watchlist after the catalog reloads (navigate away → return).
  useEffect(() => {
    if (watchlistRestoredRef.current) return;
    if (loading) return;
    if (activeWatchlistId == null) {
      watchlistRestoredRef.current = true;
      return;
    }
    if (savedViews.length === 0) {
      // Catalog loaded empty or still empty — drop stale id once load finished.
      watchlistRestoredRef.current = true;
      setActiveWatchlistId(null);
      writeActiveWatchlistId(null);
      return;
    }
    const v = savedViews.find((x) => x.id === activeWatchlistId);
    if (!v) {
      watchlistRestoredRef.current = true;
      setActiveWatchlistId(null);
      writeActiveWatchlistId(null);
      return;
    }
    applySavedView(v);
    watchlistRestoredRef.current = true;
  }, [loading, savedViews, activeWatchlistId, applySavedView]);

  const handleSaveCurrentView = async (name: string) => {
    try {
      const res = await apiService.saveTrialTrackerView({
        name,
        cat_filter: serializeMultiFilter(catFilter),
        phase_filter: serializeMultiFilter(phaseFilter),
        source_filter: serializeMultiFilter(sourceFilter),
        followed_snapshot: follows.map((f) => ({ follow_type: f.follow_type, follow_value: f.follow_value })),
        alerts_off_snapshot: alertTypesOff,
      });
      if (res?.view) {
        setSavedViews((prev) => [...prev, res.view]);
        setActiveWatchlistId(res.view.id);
        writeActiveWatchlistId(res.view.id);
      }
    } catch {
      // no-op
    }
  };

  const handleLoadView = (v: SavedView) => {
    applySavedView(v);
  };

  const handleDeleteView = async (id: number) => {
    setSavedViews((prev) => prev.filter((v) => v.id !== id));
    if (activeWatchlistId === id) {
      setActiveWatchlistId(null);
      writeActiveWatchlistId(null);
    }
    try { await apiService.deleteTrialTrackerSavedView(id); } catch { load(); }
  };

  const handleUpdateSettings = async (nextOff: Record<string, boolean>, nextDelivery: 'Instant' | 'Daily' | 'Weekly') => {
    setAlertTypesOff(nextOff);
    setDeliveryMode(nextDelivery);
    try { await apiService.updateTrialTrackerSettings({ alert_types_off: nextOff, delivery_mode: nextDelivery }); } catch { load(); }
  };

  const handleBumpAssumption = (key: keyof TrialTrackerAssumptions, delta: number) => {
    setAssumptions((prev) => {
      const next = { ...prev, [key]: Math.max(0, prev[key] + delta) };
      saveTrackerAssumptions(next);
      return next;
    });
  };

  const handleTogglePriority = (nctId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setPriorityOverrides((prev) => ({ ...prev, [nctId]: !prev[nctId] }));
  };

  const runUnifiedSearch = useCallback(async (q: string, mode: TrialTrackerSearchMode) => {
    const trimmed = q.trim();
    if (trimmed.length < 2) {
      searchAbortRef.current?.abort();
      setSearchResults(null);
      setSearchSummary(null);
      setSearchError(null);
      return;
    }
    searchAbortRef.current?.abort();
    const controller = new AbortController();
    searchAbortRef.current = controller;
    setSearchLoading(true);
    setSearchError(null);
    // Clear prior hits so the searching animation is visible immediately.
    setSearchResults(null);
    setSearchSummary(null);
    try {
      const res = await apiService.searchTrialTracker(trimmed, {
        mode,
        limit: 100,
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      if (res?.success) {
        setSearchResults(res.results || []);
        const parts = [
          `${res.local_count ?? 0} in database`,
          `${res.ctgov_count ?? 0} from CT.gov`,
        ];
        if (res.newly_inserted?.length) {
          parts.push(`${res.newly_inserted.length} newly saved`);
        }
        setSearchSummary(parts.join(' · '));
        setSearchError(res.ctgov_error || null);
      } else {
        setSearchError(res?.error || 'Search failed.');
        if (!res?.results?.length) {
          setSearchResults(null);
          setSearchSummary(null);
        }
      }
    } catch (err: unknown) {
      const ax = err as { code?: string; name?: string; message?: string; response?: { data?: { error?: string } } };
      if (ax?.code === 'ERR_CANCELED' || ax?.name === 'CanceledError' || /canceled|cancelled/i.test(ax?.message || '')) {
        return;
      }
      const timedOut = ax?.code === 'ECONNABORTED' || /timeout/i.test(ax?.message || '');
      setSearchError(
        ax?.response?.data?.error
        || (timedOut
          ? 'Search timed out — try a more specific term or NCT ID.'
          : 'Search failed — try again shortly.'),
      );
      if (timedOut) {
        setSearchSummary(null);
      } else {
        setSearchResults(null);
        setSearchSummary(null);
      }
    } finally {
      if (searchAbortRef.current === controller) {
        setSearchLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    const trimmed = submittedQuery.trim();
    if (trimmed.length < 2) {
      searchAbortRef.current?.abort();
      setSearchResults(null);
      setSearchSummary(null);
      setSearchError(null);
      setSearchLoading(false);
      return undefined;
    }
    runUnifiedSearch(trimmed, searchMode);
    return undefined;
  }, [submittedQuery, searchMode, runUnifiedSearch]);

  const handleSearchSubmit = useCallback(() => {
    const trimmed = searchQuery.trim();
    if (trimmed.length < 2) {
      setSearchError('Enter at least 2 characters, then press Enter or click Search.');
      return;
    }
    setFollowedOnly(false);
    if (trimmed === submittedQuery.trim()) {
      runUnifiedSearch(trimmed, searchMode);
      return;
    }
    setSubmittedQuery(trimmed);
  }, [searchQuery, submittedQuery, searchMode, runUnifiedSearch]);

  const handleFollowSearchTerm = async () => {
    const q = searchQuery.trim();
    if (q.length < 2) return;
    const type: 'asset' | 'indication' = searchMode === 'indication' ? 'indication' : 'asset';
    await handleAddFollow(type, q);
  };

  const handleSearchFollow = useCallback((type: 'asset' | 'indication', value: string) => {
    const q = value.trim();
    if (q.length < 2) return;
    setFollowedOnly(false);
    setSearchMode(type);
    setSearchQuery(q);
    setSubmittedQuery(q);
  }, []);

  const eventToTrial = useCallback((ev: TrialTrackerEvent): TrialTrackerTrial => ({
    nct_id: ev.nct_id,
    trial_name: ev.trial_name || ev.full_title,
    full_title: ev.full_title,
    phase: ev.phase,
    status: ev.status,
    sponsor: ev.sponsor,
    enrollment: ev.enrollment != null ? String(ev.enrollment) : null,
    study_start_date: ev.study_start_date,
    primary_completion_date: ev.primary_completion_date,
    study_completion_date: ev.study_completion_date,
    indication: ev.indication,
    indication_id: ev.indication_id,
    indication_name: ev.indication_name,
    asset_name: ev.asset_name,
    designations: ev.designations || [],
    favorited: ev.favorited,
  }), []);

  const upsertTrial = useCallback((next: TrialTrackerTrial) => {
    setTrials((prev) => {
      const idx = prev.findIndex((t) => t.nct_id === next.nct_id);
      if (idx < 0) return [...prev, next];
      const merged = { ...prev[idx], ...next };
      const copy = [...prev];
      copy[idx] = merged;
      return copy;
    });
  }, []);

  const handleSelectSearchResult = (nctId: string) => {
    handleSelectFeedItem(nctId);
    const hit = searchResults?.find((r) => r.nct_id === nctId)
      || followedTrials?.find((r) => r.nct_id === nctId);
    if (hit) upsertTrial(searchResultToTrial(hit));
  };

  useEffect(() => {
    if (!selectedNct) return undefined;
    let cancelled = false;
    apiService.fetchTrialTrackerTrial(selectedNct)
      .then((res) => {
        if (cancelled || !res?.success || !res.trial) return;
        upsertTrial(res.trial);
      })
      .catch(() => {
        // Keep feed/search hydration if the detail fetch fails.
      });
    return () => { cancelled = true; };
  }, [selectedNct, upsertTrial]);

  const handleRefreshNow = async (indicationId?: number) => {
    setRefreshing(true);
    setRefreshResult(null);
    try {
      const res = await apiService.testRunTrialTrackerRefresh({ indicationId, limit: indicationId ? 100 : 50 });
      if (res?.success) {
        setRefreshResult({ checked: res.checked, events_created: res.events_created });
        await load();
      } else {
        setRefreshResult({ checked: 0, events_created: 0, error: res?.error || 'Refresh failed.' });
      }
    } catch {
      setRefreshResult({ checked: 0, events_created: 0, error: 'Refresh failed — try again.' });
    } finally {
      setRefreshing(false);
    }
  };

  const handleFetchForIndication = (indicationName: string) => {
    const q = suggestSearchQueryForIndication(indicationName);
    setFollowedOnly(false);
    setSearchMode('indication');
    setSearchQuery(q);
    setSubmittedQuery(q);
  };

  const handleFilterBySponsor = useCallback((sponsor: string) => {
    const trimmed = sponsor.trim();
    if (!trimmed) return;
    setFollowedOnly(false);
    setSponsorFilter(trimmed);
    setQuery('');
    setSearchQuery('');
    setSubmittedQuery('');
  }, []);

  const handleClearSponsorFilter = useCallback(() => {
    setSponsorFilter(null);
  }, []);

  const handleToggleFavorites = useCallback(() => {
    setFollowedOnly(false);
    setFavOnly((v) => !v);
  }, []);

  const handleInViewSearchChange = useCallback((value: string) => {
    if (value.trim()) setFollowedOnly(false);
    setQuery(value);
  }, []);

  const handleSidebarSearchChange = useCallback((q: string) => {
    setSearchQuery(q);
    if (!q.trim()) {
      setSubmittedQuery('');
    }
  }, []);

  const selectedTrial = useMemo(() => {
    if (!selectedNct) return undefined;
    const fromTrials = trials.find((t) => t.nct_id === selectedNct)
      || feedTrials.find((t) => t.nct_id === selectedNct);
    if (fromTrials) return fromTrials;
    const hit = searchResults?.find((r) => r.nct_id === selectedNct)
      || followedTrials?.find((r) => r.nct_id === selectedNct);
    if (hit) return searchResultToTrial(hit);
    const ev = events.find((e) => e.nct_id === selectedNct);
    return ev ? eventToTrial(ev) : undefined;
  }, [selectedNct, trials, feedTrials, searchResults, followedTrials, searchResultToTrial, events, eventToTrial]);

  const drawerAllTrials = useMemo(() => {
    const source = trials.length ? trials : feedTrials;
    const seen = new Set<string>();
    return source.filter((t) => {
      if (!t.nct_id || seen.has(t.nct_id)) return false;
      seen.add(t.nct_id);
      return true;
    });
  }, [trials, feedTrials]);

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-tracker-bg">
      <Header />
      <div className="flex-1 flex min-h-0 overflow-hidden">
        <TrackerSidebar
          savedViews={savedViews}
          activeWatchlistId={activeWatchlistId}
          onSaveCurrentView={handleSaveCurrentView}
          onLoadView={handleLoadView}
          onDeleteView={handleDeleteView}
          follows={follows}
          followSuggestions={followSuggestions}
          onAddFollow={handleAddFollow}
          onRemoveFollow={handleRemoveFollow}
          onSearchFollow={handleSearchFollow}
          catFilter={catFilter}
          onToggleCatFilter={handleToggleCatFilter}
          phaseOptions={phaseOptions}
          phaseFilter={phaseFilter}
          onTogglePhaseFilter={handleTogglePhaseFilter}
          statusFilter={statusFilter}
          onToggleStatusFilter={handleToggleStatusFilter}
          sourceOptions={sourceOptions}
          sourceFilter={sourceFilter}
          onToggleSourceFilter={handleToggleSourceFilter}
          alertTypesOff={alertTypesOff}
          deliveryMode={deliveryMode}
          onUpdateSettings={handleUpdateSettings}
          indications={indications}
          indicationFilter={indicationFilter}
          onSetIndicationFilter={handleSetIndicationFilter}
          searchQuery={searchQuery}
          searchMode={searchMode}
          onSearchQueryChange={handleSidebarSearchChange}
          onSearchSubmit={handleSearchSubmit}
          onSearchModeChange={setSearchMode}
          searchLoading={searchLoading}
          searchError={searchError}
          searchSummary={searchSummary}
          onFollowSearchTerm={handleFollowSearchTerm}
          isSuperuser={isSuperuser}
          selectedIndicationMeta={selectedIndicationMeta}
          onRefreshNow={() => handleRefreshNow()}
          onRefreshIndication={selectedIndicationMeta ? () => handleRefreshNow(selectedIndicationMeta.id) : undefined}
          onFetchForIndication={selectedIndicationMeta ? () => handleFetchForIndication(selectedIndicationMeta.name) : undefined}
          refreshing={refreshing}
          refreshResult={refreshResult}
        />

        <div className="flex-1 flex min-w-0 min-h-0 overflow-hidden">
          <div className="flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden">
          <div className="flex-none px-6 pt-4 pb-3 border-b border-tracker-line bg-white shrink-0">
            <div className="flex items-center gap-2.5">
              <div className="flex gap-0.5 bg-tracker-line3 border border-tracker-line rounded-lg p-0.5 flex-none">
                {(['feed', 'timeline'] as ViewMode[]).map((v) => (
                  <button
                    key={v}
                    onClick={() => setView(v)}
                    className={`px-3.5 py-1.5 rounded-md text-xs font-semibold capitalize ${view === v ? 'bg-white shadow-sm text-tracker-accent' : 'text-tracker-muted'}`}
                  >
                    {v === 'feed' ? 'Alert feed' : 'Timeline'}
                  </button>
                ))}
              </div>
              <span className="text-[11.5px] text-tracker-muted whitespace-nowrap">
                {searchActive ? (
                  <>
                    {resultCount} search result{resultCount === 1 ? '' : 's'}
                    {searchSummary ? ` · ${searchSummary}` : ''}
                  </>
                ) : followedViewActive ? (
                  <>
                    {resultCount} followed trial{resultCount === 1 ? '' : 's'}
                    {follows.length > 0 ? ` · ${follows.length} follow${follows.length === 1 ? '' : 's'}` : ''}
                  </>
                ) : (
                  <>
                    {view === 'feed' && snapshotCount > 0
                      ? `${resultCount} trial${resultCount === 1 ? '' : 's'}${alertCount ? ` · ${alertCount} alert${alertCount === 1 ? '' : 's'}` : ''}`
                      : `${resultCount} ${view === 'feed' ? (resultCount === 1 ? 'alert' : 'alerts') : (resultCount === 1 ? 'trial' : 'trials')}`}
                  </>
                )}
                {sponsorFilter && (
                  <> · company: <span className="font-semibold text-sky-700">{sponsorFilter}</span></>
                )}
                {lastScrapeAt && (
                  <> · data as of <span className="font-plex text-tracker-muted">{formatScrapeDate(lastScrapeAt)}</span></>
                )}
              </span>
              <div className="ml-auto flex items-center gap-2 flex-none">
                {sponsorFilter && (
                  <button
                    type="button"
                    onClick={handleClearSponsorFilter}
                    className="text-[11.5px] font-semibold text-sky-800 bg-sky-50 border border-sky-200 rounded-lg px-2.5 py-1.5 whitespace-nowrap max-w-[12rem] truncate"
                    title="Clear company filter"
                  >
                    {sponsorFilter} ×
                  </button>
                )}
                {unreadCount > 0 && (
                  <button onClick={handleMarkAllRead} className="text-[11.5px] font-semibold text-tracker-accent bg-tracker-statusBg border border-tracker-cardBorderUnread rounded-lg px-2.5 py-1.5 whitespace-nowrap">
                    ✓ Mark all read
                  </button>
                )}
                <button
                  onClick={() => setFollowedOnly((v) => !v)}
                  className={`text-[11.5px] font-semibold rounded-lg px-2.5 py-1.5 whitespace-nowrap border ${followedOnly ? 'bg-tracker-accent border-tracker-accent text-white' : 'bg-white border-tracker-line text-tracker-muted'}`}
                >
                  Followed only
                </button>
                <button
                  onClick={handleToggleFavorites}
                  title="Show only favorited trials"
                  className={`text-[11.5px] font-semibold rounded-lg px-2.5 py-1.5 whitespace-nowrap border ${favOnly ? 'bg-tracker-favBgActive border-tracker-favBorderActive text-tracker-favFgActive' : 'bg-white border-tracker-line text-tracker-muted'}`}
                >
                  ★ Favorites
                </button>
                <div className="flex items-center gap-1.5 bg-tracker-bg border border-tracker-line rounded-lg px-2.5 h-[33px] w-56">
                  <span className="text-tracker-muted text-xs">⌕</span>
                  <input
                    type="text"
                    placeholder="Search trials, assets, sponsors…"
                    value={query}
                    onChange={(e) => handleInViewSearchChange(e.target.value)}
                    className="flex-1 min-w-0 border-none outline-none bg-transparent text-xs text-tracker-ink"
                  />
                </div>
              </div>
            </div>
          </div>

          {loading ? (
            <TrackerLoadingPanel
              message="Loading trial tracker…"
              detail="Fetching alerts, trials, and your saved views."
            />
          ) : error ? (
            <div className="flex-1 flex items-center justify-center text-sm text-tracker-muted">{error}</div>
          ) : searchActive ? (
            <TrackerSearchResults
              results={filteredSearchResults}
              loading={searchLoading}
              error={searchError}
              summary={null}
              query={submittedQuery}
              selectedNct={selectedNct}
              onSelect={handleSelectSearchResult}
              onFilterBySponsor={handleFilterBySponsor}
            />
          ) : followedViewActive ? (
            view === 'timeline' ? (
              followedLoading ? (
                <TrackerLoadingPanel
                  message="Loading followed trials…"
                  detail="Pulling trials for the assets and indications you follow."
                />
              ) : followedError ? (
                <div className="flex-1 flex items-center justify-center text-sm text-tracker-muted">{followedError}</div>
              ) : followedTimelineTrials.length === 0 ? (
                <div className="flex-1 flex items-center justify-center text-sm text-tracker-muted px-6 text-center">
                  {follows.length === 0
                    ? 'Follow an asset or indication in the sidebar to see trials here.'
                    : 'No followed trials match the current filters — try Trial Status → All.'}
                </div>
              ) : (
                <TrackerTimeline
                  trials={followedTimelineTrials}
                  showGroupHeaders={showGroupHeaders}
                  assumptions={assumptions}
                  onBumpAssumption={handleBumpAssumption}
                  onSelect={handleSelectSearchResult}
                  priorityOverrides={priorityOverrides}
                  onTogglePriority={handleTogglePriority}
                  onFilterBySponsor={handleFilterBySponsor}
                />
              )
            ) : (
              <TrackerSearchResults
                results={filteredFollowedTrials}
                loading={followedLoading}
                error={followedError}
                summary={
                  follows.length === 0
                    ? 'Follow an asset or indication in the sidebar to see trials here.'
                    : filteredFollowedTrials.length === 0
                      ? 'No followed trials match the current filters — try Trial Status → All.'
                      : undefined
                }
                query=""
                selectedNct={selectedNct}
                onSelect={handleSelectSearchResult}
                onFilterBySponsor={handleFilterBySponsor}
              />
            )
          ) : view === 'feed' ? (
            <div className="flex-1 overflow-y-auto p-5 relative">
              {feedRefreshing && (
                <div className="sticky top-0 z-10 mb-3 flex items-center gap-2 rounded-lg border border-tracker-cardBorderUnread bg-tracker-statusBg/95 px-3 py-2 text-[11.5px] font-semibold text-tracker-accent shadow-sm backdrop-blur-sm animate-fadeIn">
                  <span className="relative h-3.5 w-3.5 flex-none">
                    <span className="absolute inset-0 rounded-full border border-tracker-accent/30" />
                    <span className="absolute inset-0 rounded-full border border-transparent border-t-tracker-accent animate-spin" />
                  </span>
                  Updating feed…
                </div>
              )}
              {trialsTruncated && (
                <div className="mb-3 text-[11.5px] text-tracker-muted bg-white border border-tracker-line rounded-lg px-3 py-2">
                  {indicationFilter.length === 0
                    ? 'Some indications have more trials than shown here — select an indication to see every row.'
                    : 'This indication has more trials than the feed can show in one page.'}
                </div>
              )}
              <div className={`transition-opacity duration-300 ${feedRefreshing ? 'opacity-50' : 'opacity-100'}`}>
                <TrackerFeed
                  groups={groupedEvents}
                  showGroupHeaders={showGroupHeaders}
                  selectedNct={selectedNct}
                  onSelect={handleSelectFeedItem}
                  onToggleFavorite={handleToggleFavorite}
                  onFilterBySponsor={handleFilterBySponsor}
                />
              </div>
            </div>
          ) : indicationFilter.length === 0 ? (
            <div className="flex-1 flex items-center justify-center px-8">
              <div className="max-w-md text-center">
                <div className="text-sm font-semibold text-tracker-ink">Select an indication for the timeline</div>
                <div className="text-xs text-tracker-muted mt-2 leading-relaxed">
                  All-indications mode loads thousands of trial rows — too many for a readable Gantt.
                  Pick one indication in the sidebar to see every trial on the timeline
                  {timelineTrials.length > 0 ? ` (${timelineTrials.length.toLocaleString()} are loaded in the feed preview).` : '.'}
                </div>
              </div>
            </div>
          ) : feedRefreshing && timelineTrials.length === 0 ? (
            <TrackerLoadingPanel
              message="Loading timeline…"
              detail="Fetching trials for the selected indication."
            />
          ) : (
            <div className="flex-1 flex flex-col min-h-0 relative">
              {feedRefreshing && (
                <div className="mx-5 mt-2 mb-0 flex items-center gap-2 rounded-lg border border-tracker-cardBorderUnread bg-tracker-statusBg/95 px-3 py-2 text-[11.5px] font-semibold text-tracker-accent shadow-sm animate-fadeIn">
                  <span className="relative h-3.5 w-3.5 flex-none">
                    <span className="absolute inset-0 rounded-full border border-tracker-accent/30" />
                    <span className="absolute inset-0 rounded-full border border-transparent border-t-tracker-accent animate-spin" />
                  </span>
                  Updating timeline…
                </div>
              )}
              {trialsTruncated && (
                <div className="mx-5 mt-2 mb-0 text-[11.5px] text-tracker-muted bg-white border border-tracker-line rounded-lg px-3 py-2">
                  This indication has more trials than the feed can show in one page.
                </div>
              )}
              <div className={`flex-1 flex flex-col min-h-0 transition-opacity duration-300 ${feedRefreshing ? 'opacity-50' : 'opacity-100'}`}>
                <TrackerTimeline
                  trials={timelineTrials}
                  showGroupHeaders={showGroupHeaders}
                  assumptions={assumptions}
                  onBumpAssumption={handleBumpAssumption}
                  onSelect={handleSelectFeedItem}
                  priorityOverrides={priorityOverrides}
                  onTogglePriority={handleTogglePriority}
                  onFilterBySponsor={handleFilterBySponsor}
                />
              </div>
            </div>
          )}
          </div>

          {selectedNct && (
            <TrackerDrawer
              nctId={selectedNct}
              trial={selectedTrial}
              events={events}
              allTrials={drawerAllTrials}
              onClose={() => setSelectedNct(null)}
              onSelectSimilar={setSelectedNct}
              onFilterBySponsor={handleFilterBySponsor}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default TrialTrackerView;
